import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import apiAxios, { apiAxiosUpload } from "helpers/apiAxios";
import { handleAsyncThunkError, addRejectedMatcher } from "../reduxHelpers";

const BASE = "/erp/payment_requests";

export const getPaymentFilePreview = createAsyncThunk(
  "PaymentRequest/getPaymentFilePreview",
  async (id, { rejectWithValue }) => {
    try { return (await apiAxios.get(`${BASE}/${id}/payment_file_preview`)).data; }
    catch (error) { return rejectWithValue(handleAsyncThunkError(error, "Could not load payment mapping.")); }
  }
);

export const exportPaymentFiles = createAsyncThunk(
  "PaymentRequest/exportPaymentFiles",
  async (id, { rejectWithValue }) => {
    try { return (await apiAxios.post(`${BASE}/${id}/payment_files`)).data; }
    catch (error) { return rejectWithValue(handleAsyncThunkError(error, "Could not export payment files.")); }
  }
);

export const getPaymentRequests = createAsyncThunk(
  "PaymentRequest/getPaymentRequests",
  async (params = {}, { rejectWithValue }) => {
    try {
      const res = await apiAxios.get(BASE, { params });
      return res.data;
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, "Failed to load payment requests."));
    }
  }
);

export const getPaymentRequestInbox = createAsyncThunk(
  "PaymentRequest/getPaymentRequestInbox",
  async (params = {}, { rejectWithValue }) => {
    try {
      const res = await apiAxios.get(`${BASE}/inbox`, { params });
      return res.data;
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, "Failed to load approval inbox."));
    }
  }
);

export const getPaymentRequest = createAsyncThunk(
  "PaymentRequest/getPaymentRequest",
  async (id, { rejectWithValue }) => {
    try {
      const res = await apiAxios.get(`${BASE}/${id}`);
      return res.data;
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, "Failed to load payment request."));
    }
  }
);

export const createPaymentRequest = createAsyncThunk(
  "PaymentRequest/createPaymentRequest",
  async (data, { rejectWithValue }) => {
    try {
      const res = await apiAxios.post(BASE, data);
      return res.data;
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, "Failed to create payment request."));
    }
  }
);

export const updatePaymentRequest = createAsyncThunk(
  "PaymentRequest/updatePaymentRequest",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const res = await apiAxios.put(`${BASE}/${id}`, data);
      return res.data;
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, "Failed to update payment request."));
    }
  }
);

export const deletePaymentRequest = createAsyncThunk(
  "PaymentRequest/deletePaymentRequest",
  async (id, { rejectWithValue }) => {
    try {
      const res = await apiAxios.delete(`${BASE}/${id}`);
      return { id, ...res.data };
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, "Failed to delete payment request."));
    }
  }
);

export const submitPaymentRequest = createAsyncThunk(
  "PaymentRequest/submitPaymentRequest",
  async (id, { rejectWithValue }) => {
    try {
      const res = await apiAxios.post(`${BASE}/${id}/submit`);
      return res.data;
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, "Failed to submit for approval."));
    }
  }
);

// Người lập rút phiếu về nháp để bổ sung (chỉ khi Manager CHƯA ký).
export const withdrawPaymentRequest = createAsyncThunk(
  "PaymentRequest/withdrawPaymentRequest",
  async (id, { rejectWithValue }) => {
    try {
      const res = await apiAxios.post(`${BASE}/${id}/withdraw`);
      return res.data;
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, "Failed to withdraw the request."));
    }
  }
);

export const approvePaymentRequest = createAsyncThunk(
  "PaymentRequest/approvePaymentRequest",
  async ({ id, comment }, { rejectWithValue }) => {
    try {
      const res = await apiAxios.post(`${BASE}/${id}/approve`, { comment });
      return res.data;
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, "Approval failed."));
    }
  }
);

export const rejectPaymentRequest = createAsyncThunk(
  "PaymentRequest/rejectPaymentRequest",
  async ({ id, comment }, { rejectWithValue }) => {
    try {
      const res = await apiAxios.post(`${BASE}/${id}/reject`, { comment });
      return res.data;
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, "Rejection failed."));
    }
  }
);

// Cancel — người lập tự huỷ hẳn phiếu (draft/pending), khác Withdraw (rút về draft để sửa).
export const cancelPaymentRequest = createAsyncThunk(
  "PaymentRequest/cancelPaymentRequest",
  async ({ id, comment }, { rejectWithValue }) => {
    try {
      const res = await apiAxios.post(`${BASE}/${id}/cancel`, { comment });
      return res.data;
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, "Cancel failed."));
    }
  }
);

// Duplicate — tạo 1 Payment Request mới ở Draft, copy nội dung từ phiếu này.
export const duplicatePaymentRequest = createAsyncThunk(
  "PaymentRequest/duplicatePaymentRequest",
  async (id, { rejectWithValue }) => {
    try {
      const res = await apiAxios.post(`${BASE}/${id}/duplicate`);
      return res.data;
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, "Duplicate failed."));
    }
  }
);

// Kế toán viên submit phiếu sang ERP (Ax), mode "app" | "api".
export const submitPaymentRequestErp = createAsyncThunk(
  "PaymentRequest/submitPaymentRequestErp",
  async ({ id, mode }, { rejectWithValue }) => {
    try {
      const res = await apiAxios.post(`${BASE}/${id}/submit_to_erp`, { mode });
      return res.data;
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, "ERP submit failed."));
    }
  }
);

// apiAxios mặc định Content-Type: application/json -> FormData sẽ hỏng boundary.
// Dùng apiAxiosUpload (multipart + timeout dài) như các luồng upload khác.
export const uploadPaymentRequestDocument = createAsyncThunk(
  "PaymentRequest/uploadPaymentRequestDocument",
  async ({ id, formData }, { rejectWithValue }) => {
    try {
      const res = await apiAxiosUpload.post(`${BASE}/${id}/upload_document`, formData);
      return res.data;
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, "Document upload failed."));
    }
  }
);

// Kế toán trưởng chỉ định người ký cuối (BOD) cho phiếu.
export const setPaymentRequestFinalSigner = createAsyncThunk(
  "PaymentRequest/setPaymentRequestFinalSigner",
  async ({ id, userId, reason }, { rejectWithValue }) => {
    try {
      const res = await apiAxios.post(`${BASE}/${id}/final_signer`, { user_id: userId, reason });
      return res.data;
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, "Could not update the final signer."));
    }
  }
);

// Đổi ngân hàng xử lý (HSBC / Non-HSBC). Đổi sang HSBC trên phiếu đã có chuỗi
// duyệt sẽ mở một lượt duyệt mới cho Head of Finance + BOD.
export const changePaymentRequestBankRoute = createAsyncThunk(
  "PaymentRequest/changeBankRoute",
  async ({ id, bankRoute, reason }, { rejectWithValue }) => {
    try {
      const res = await apiAxios.post(`${BASE}/${id}/bank_route`, { bank_route: bankRoute, reason });
      return res.data;
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, "Could not change the bank."));
    }
  }
);

// Gỡ phiếu khỏi trạng thái "chưa rõ HSBC đã nhận lệnh chưa". Có reference = HSBC
// xác nhận ĐÃ nhận (ghi nhận để theo dõi tiếp); không reference = HSBC xác nhận
// KHÔNG nhận (mở lại đường gửi). Kết luận sai ở đây là chi trùng, nên bắt buộc lý do.
// The release after BOD failed: send the same signed payment to HSBC again (Accounting / admin).
export const retryPaymentRequestRelease = createAsyncThunk(
  "PaymentRequest/retryRelease",
  async ({ id, reason }, { rejectWithValue }) => {
    try {
      const res = await apiAxios.post(`${BASE}/${id}/retry_release`, { reason });
      return res.data;
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, "Could not send the payment again."));
    }
  }
);

// Payment Accountant: a Non-HSBC payment was made outside the Portal — record it as paid.
export const markPaymentRequestDisbursementPaid = createAsyncThunk(
  "PaymentRequest/markDisbursementPaid",
  async ({ id, reference, note }, { rejectWithValue }) => {
    try {
      const res = await apiAxios.post(`${BASE}/${id}/disbursement_paid`, { reference, note });
      return res.data;
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, "Could not record the payment."));
    }
  }
);

export const reopenPaymentRequestBankResolution = createAsyncThunk(
  "PaymentRequest/reopenBankResolution",
  async ({ id, reason }, { rejectWithValue }) => {
    try {
      const res = await apiAxios.post(`${BASE}/${id}/reopen_bank_resolution`, { reason });
      return res.data;
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, "Could not undo the recorded HSBC result."));
    }
  }
);

export const resolvePaymentRequestBankUncertainty = createAsyncThunk(
  "PaymentRequest/resolveBankUncertainty",
  async ({ id, reference, reason }, { rejectWithValue }) => {
    try {
      const res = await apiAxios.post(`${BASE}/${id}/resolve_bank_uncertainty`, { reference, reason });
      return res.data;
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, "Could not resolve the bank instruction."));
    }
  }
);

export const submitPaymentRequestBankInstruction = createAsyncThunk(
  "PaymentRequest/submitBankInstruction",
  async (id, { rejectWithValue }) => {
    try {
      const res = await apiAxios.post(`${BASE}/${id}/bank_instruction`);
      return res.data;
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, "Could not send the bank instruction."));
    }
  }
);

export const refreshPaymentRequestBankStatus = createAsyncThunk(
  "PaymentRequest/refreshBankStatus",
  async (id, { rejectWithValue }) => {
    try {
      const res = await apiAxios.get(`${BASE}/${id}/bank_status`);
      return res.data;
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, "Could not read the bank status."));
    }
  }
);

export const requestPaymentRequestSupplement = createAsyncThunk(
  "PaymentRequest/requestSupplement",
  async ({ id, note }, { rejectWithValue }) => {
    try {
      const res = await apiAxios.post(`${BASE}/${id}/request_supplement`, { note });
      return res.data;
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, "Could not request the supplement."));
    }
  }
);

// ─── Purchase Order ─────────────────────────────────────────────────────────
// Kế toán phải link 1 PO (bên AX) trước khi tạo Journal — Journal dựng từ vendor,
// tài khoản chi phí, thuế và 3 chiều phân tích của PO.

export const searchPurchaseOrders = createAsyncThunk(
  "PaymentRequest/searchPurchaseOrders",
  async (params = {}, { rejectWithValue }) => {
    try {
      const res = await apiAxios.get(`${BASE}/purchase_order_search`, { params });
      return res.data;
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, "Could not search purchase orders."));
    }
  }
);

export const linkPurchaseOrder = createAsyncThunk(
  "PaymentRequest/linkPurchaseOrder",
  async ({ id, poNum }, { rejectWithValue }) => {
    try {
      const res = await apiAxios.post(`${BASE}/${id}/link_po`, { po_num: poNum });
      return res.data;
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, "Could not link the purchase order."));
    }
  }
);

export const unlinkPurchaseOrder = createAsyncThunk(
  "PaymentRequest/unlinkPurchaseOrder",
  async (id, { rejectWithValue }) => {
    try {
      const res = await apiAxios.post(`${BASE}/${id}/unlink_po`);
      return res.data;
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, "Could not unlink the purchase order."));
    }
  }
);

// Xem trước bút toán sẽ tạo. Không ghi gì — kết quả giữ ở state của component.
export const getJournalPreview = createAsyncThunk(
  "PaymentRequest/getJournalPreview",
  async (id, { rejectWithValue }) => {
    try {
      const res = await apiAxios.get(`${BASE}/${id}/journal_preview`);
      return res.data;
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, "Could not build the journal preview."));
    }
  }
);

// ── Tab "Bút toán & chứng từ" ──────────────────────────────────────────────
// Kế toán / Kế toán trưởng điền bổ sung bút toán và đẩy sang ERP qua API. Mọi
// thunk dưới đây trả { data: workbench, message, warning?, warnings? }; kết quả
// giữ ở state của JournalWorkbench, không đụng recordData.
const journalThunk = (type, fallback, call) =>
  createAsyncThunk(`PaymentRequest/${type}`, async (args, { rejectWithValue }) => {
    try {
      const res = await call(args);
      return res.data;
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, fallback));
    }
  });

export const getJournalWorkbench = journalThunk(
  "getJournalWorkbench", "Could not load the journal.",
  (id) => apiAxios.get(`${BASE}/${id}/journal_workbench`)
);
export const saveJournalOverrides = journalThunk(
  "saveJournalOverrides", "Could not save the journal details.",
  // journal_lines: lines of the request's other journals, e.g. { APP: [...] }.
  // journal_headers: each further journal's own header; options: Journal name /
  // Payment journal on-off. Everything the tab sends must be listed here.
  ({ id, lines, header, payment, journal_lines, journal_headers, options }) =>
    apiAxios.put(`${BASE}/${id}/journal_overrides`, { lines, header, payment, journal_lines, journal_headers, options })
);
export const createJournalFromWorkbench = journalThunk(
  "createJournalFromWorkbench", "Could not create the journal in ERP.",
  (id) => apiAxios.post(`${BASE}/${id}/journal_create`)
);
export const pushJournalChanges = journalThunk(
  "pushJournalChanges", "Could not update the journal in ERP.",
  // Accepts the request id (primary journal) or { id, journal } for a given journal.
  (arg) => {
    const { id, journal } = typeof arg === "object" ? arg : { id: arg };
    return apiAxios.post(`${BASE}/${id}/journal_push`, journal ? { journal } : undefined);
  }
);
// Mở khoá Payment journal (APP) sau khi HoF/BOD đã ký — lưu thay đổi = HoF + BOD ký lại.
export const startJournalAdjustment = journalThunk(
  "startJournalAdjustment", "Could not unlock the Payment journal.",
  ({ id, reason }) => apiAxios.post(`${BASE}/${id}/journal_adjustment`, { reason })
);
export const cancelJournalAdjustment = journalThunk(
  "cancelJournalAdjustment", "Could not cancel the adjustment.",
  (id) => apiAxios.delete(`${BASE}/${id}/journal_adjustment`)
);
// Chứng từ kế toán (kind = accounting) — trả về chi tiết phiếu như upload_document.
export const uploadAccountingDocument = journalThunk(
  "uploadAccountingDocument", "Document upload failed.",
  ({ id, formData }) => apiAxiosUpload.post(`${BASE}/${id}/accounting_documents`, formData)
);
export const deleteAccountingDocument = journalThunk(
  "deleteAccountingDocument", "Failed to remove document.",
  ({ id, documentId }) => apiAxios.delete(`${BASE}/${id}/accounting_documents/${documentId}`)
);

// Gỡ một tài liệu khỏi phiếu (cả hóa đơn doc-platform lẫn file đã upload).
export const deletePaymentRequestDocument = createAsyncThunk(
  "PaymentRequest/deletePaymentRequestDocument",
  async ({ id, documentId }, { rejectWithValue }) => {
    try {
      const res = await apiAxios.delete(`${BASE}/${id}/documents/${documentId}`);
      return res.data;
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, "Failed to remove document."));
    }
  }
);

// Search invoices via doc-flatform proxy.
export const searchInvoices = createAsyncThunk(
  "PaymentRequest/searchInvoices",
  async (params = {}, { rejectWithValue }) => {
    try {
      const res = await apiAxios.get(`${BASE}/invoice_search`, { params });
      return res.data; // { items, total }
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, "Invoice search failed."));
    }
  }
);

// Invoice detail for auto-fill.
export const getInvoiceDetail = createAsyncThunk(
  "PaymentRequest/getInvoiceDetail",
  async (documentId, { rejectWithValue }) => {
    try {
      // skipErrorToast: giữ nguyên body 404 của BE (hoá đơn mồ côi trên Doc Search)
      // thay vì câu chung "Resource not found." của interceptor.
      // ERP invoices (From PO ERP: "erp:<vendor>:<invoice no>") have no Doc Search record
      // and no file to show — and their number may contain "/", which broke the route.
      if (String(documentId).startsWith("erp:")) return null;
      const res = await apiAxios.get(`${BASE}/invoice_detail/${encodeURIComponent(documentId)}`, { skipErrorToast: true });
      return res.data?.data;
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, "Failed to load invoice."));
    }
  }
);

// Ký hàng loạt từ tab "Waiting For Approval". BE luôn trả 200 kèm
// { approved, failed } — 1 phiếu lỗi không làm hỏng cả lô, nên caller phải đọc
// `failed` chứ không chỉ dựa vào việc thunk có reject hay không.
export const bulkApprovePaymentRequests = createAsyncThunk(
  "PaymentRequest/bulkApprovePaymentRequests",
  async ({ ids, comment }, { rejectWithValue }) => {
    try {
      const res = await apiAxios.post(`${BASE}/bulk_approve`, { ids, comment });
      return res.data;
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, "Bulk approval failed."));
    }
  }
);

// Giá trị thật cho combobox lọc (department / requestor / form type / status).
// Badge số phiếu trên từng tab của màn danh sách — theo khoảng ngày, không theo filter khác.
export const getPaymentRequestTabCounts = createAsyncThunk(
  "PaymentRequest/getPaymentRequestTabCounts",
  async (params = {}, { rejectWithValue }) => {
    try {
      const res = await apiAxios.get(`${BASE}/tab_counts`, { params });
      return res.data?.data;
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, "Failed to load tab counts."));
    }
  }
);

export const getPaymentRequestFilterOptions = createAsyncThunk(
  "PaymentRequest/getPaymentRequestFilterOptions",
  async (_, { rejectWithValue }) => {
    try {
      const res = await apiAxios.get(`${BASE}/filter_options`);
      return res.data?.data;
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, "Failed to load filter options."));
    }
  }
);

// Bút toán mồ côi: phiếu đã huỷ/từ chối nhưng chứng từ vẫn nằm trong sổ ERP.
// Portal KHÔNG có nghiệp vụ đảo bút toán — kế toán phải làm tay bên AX, nên danh
// sách này là đường duy nhất để họ nhìn thấy khoản còn treo.
// Quyền: kế toán / kế toán công nợ / admin (người thường nhận 403).
// Kết quả KHÔNG đưa vào store: đây là màn tra cứu mở ra rồi đóng, giữ trong state
// của component để không bao giờ hiện một danh sách cũ đã đóng lại.
export const getOrphanedJournals = createAsyncThunk(
  "PaymentRequest/getOrphanedJournals",
  async (_, { rejectWithValue }) => {
    try {
      const res = await apiAxios.get(`${BASE}/orphaned_journals`);
      return res.data;
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, "Could not load orphaned ERP journals."));
    }
  }
);

// Đóng một dòng mồ côi: kế toán đã đảo bút toán bên AX và khai lại chứng từ đảo.
// Chỉ GHI NHẬN — Portal không gọi sang AX để đảo, và không được tỏ ra như đã đảo.
// Hợp đồng params theo backend: { reference, note }.
export const markPaymentRequestJournalReversed = createAsyncThunk(
  "PaymentRequest/markPaymentRequestJournalReversed",
  async ({ id, reference, note }, { rejectWithValue }) => {
    try {
      const res = await apiAxios.post(`${BASE}/${id}/mark_journal_reversed`, { reference, note });
      return res.data;
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, "Could not record the reversal."));
    }
  }
);

// HSBC online payment file (XML v3 pain.001 + GPG): status, review checklist, mapping.
export const getHsbcPackage = journalThunk(
  "getHsbcPackage", "Could not load the HSBC payment file.",
  // A panel on the request page: a 403 here must not replace the whole page (BOD, 2026-10-22).
  (id) => apiAxios.get(`${BASE}/${id}/hsbc_package`, { skipAccessDeniedPage: true })
);
export const buildHsbcPackage = journalThunk(
  "buildHsbcPackage", "Could not prepare the HSBC payment file.",
  (id) => apiAxios.post(`${BASE}/${id}/hsbc_package`)
);
// One HSBC file as text, for the viewer: kind = pain (XML v3) · gpg · instruction.
export const getHsbcPackageFile = journalThunk(
  "getHsbcPackageFile", "Could not load the file.",
  ({ id, kind }) => apiAxios.get(`${BASE}/${id}/hsbc_package/${kind}/view`, { skipAccessDeniedPage: true })
);
// Timeline of the request (audits), newest first.
export const getPaymentRequestActivity = journalThunk(
  "getPaymentRequestActivity", "Could not load the activity.",
  (id) => apiAxios.get(`${BASE}/${id}/activity`, { skipAccessDeniedPage: true })
);

// kind: instruction (AxPaymentRequest) · pain (pain.001 XML v3) · gpg (encrypted).
export const downloadHsbcPackageFile = async (id, kind) => {
  const res = await apiAxios.get(`${BASE}/${id}/hsbc_package/${kind}`, { responseType: "blob" });
  const cd = res.headers?.["content-disposition"] || "";
  const match = cd.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
  const url = window.URL.createObjectURL(new Blob([res.data], { type: res.data.type || "application/octet-stream" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = match ? decodeURIComponent(match[1]) : `payment-request-${id}-${kind}`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

// Bản in: backend sinh PDF theo mẫu QF-FNC (có chữ ký) rồi trả về.
// Dùng apiAxios để đính token, nhận blob PDF rồi mở tab mới để xem/in.
export const openPaymentRequestPdf = async (id) => {
  const res = await apiAxios.get(`${BASE}/${id}/print`, { responseType: "blob" });
  const url = window.URL.createObjectURL(new Blob([res.data], { type: res.data.type || "application/pdf" }));
  // Mở tab mới để xem PDF; nếu bị popup-block thì fallback sang tải file.
  const win = window.open(url, "_blank");
  if (!win) {
    const cd = res.headers?.["content-disposition"] || "";
    const match = cd.match(/filename="?([^"]+)"?/);
    const a = document.createElement("a");
    a.href = url;
    a.download = match ? match[1] : `payment-request-${id}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  }
  setTimeout(() => window.URL.revokeObjectURL(url), 60000);
};

// Request bị huỷ (người dùng gõ tiếp ký tự kế) không phải lỗi — handleAsyncThunkError
// đánh dấu silent/cancelled cho đúng trường hợp này.
const errorFrom = (payload) => {
  if (!payload || payload.silent || payload.cancelled) return null;
  if (typeof payload === "string") return "Could not load payment requests.";
  return payload.error || payload.message || "Could not load payment requests.";
};

const EMPTY_LIST = { rows: [], total_rows: 0, pagination: null };

const initialState = {
  allData: EMPTY_LIST,
  inboxData: EMPTY_LIST,
  filterOptions: { departments: [], requestors: [], form_types: [], statuses: [] },
  tabCounts: {},
  // All / Mine / Approved / Rejected / Unpaid HSBC dùng chung allData: chỉ nhận
  // response của lần gọi mới nhất, kẻo đổi tab nhanh thì list tab cũ đè lên tab mới.
  listRequestId: null,
  recordData: {},
  // Mỗi tab một cờ loading riêng. Trước đây cả 3 list dùng chung `loading` và
  // getPaymentRequestInbox không có case `.pending`, nên đổi sang tab inbox là
  // màn hình nháy "No payment requests" (loading=false + inboxData rỗng) trước
  // khi response về.
  listLoading: false,
  inboxLoading: false,
  // Lỗi tách riêng theo tab: nếu chỉ dựa vào rows rỗng thì "API hỏng" và
  // "không có phiếu nào" hiện ra y hệt nhau — đúng cái bẫy đã gặp khi API 500
  // vì pending migration mà màn hình vẫn báo "No payment requests".
  listError: null,
  inboxError: null,
  loading: false,          // chỉ còn dùng cho recordData (trang detail)
  bulkApproving: false,
  error: null,
};

const paymentRequestSlice = createSlice({
  name: "PaymentRequest",
  initialState,
  reducers: {
    clearPaymentRequest: (state) => {
      state.recordData = {};
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getPaymentRequests.pending, (state, action) => {
        state.listLoading = true;
        state.listError = null;
        state.listRequestId = action.meta.requestId;
      })
      .addCase(getPaymentRequests.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.listRequestId) return;
        state.listLoading = false;
        state.allData = action.payload;
      })
      .addCase(getPaymentRequests.rejected, (state, action) => {
        if (action.meta.requestId !== state.listRequestId) return;
        state.listLoading = false;
        state.listError = errorFrom(action.payload);
      })
      .addCase(getPaymentRequestTabCounts.fulfilled, (state, action) => { state.tabCounts = action.payload || {}; })
      .addCase(getPaymentRequestInbox.pending, (state) => { state.inboxLoading = true; state.inboxError = null; })
      .addCase(getPaymentRequestInbox.fulfilled, (state, action) => {
        state.inboxLoading = false;
        state.inboxData = action.payload;
      })
      .addCase(getPaymentRequestInbox.rejected, (state, action) => { state.inboxLoading = false; state.inboxError = errorFrom(action.payload); })
      .addCase(getPaymentRequestFilterOptions.fulfilled, (state, action) => {
        state.filterOptions = action.payload || initialState.filterOptions;
      })
      .addCase(bulkApprovePaymentRequests.pending, (state) => { state.bulkApproving = true; })
      .addCase(bulkApprovePaymentRequests.fulfilled, (state) => { state.bulkApproving = false; })
      .addCase(bulkApprovePaymentRequests.rejected, (state) => { state.bulkApproving = false; })
      .addCase(getPaymentRequest.pending, (state) => { state.loading = true; })
      .addCase(getPaymentRequest.rejected, (state) => { state.loading = false; })
      .addCase(getPaymentRequest.fulfilled, (state, action) => {
        state.loading = false;
        state.recordData = action.payload?.data?.attributes || action.payload?.data || {};
      })
      .addCase(createPaymentRequest.fulfilled, (state, action) => {
        state.recordData = action.payload?.data?.attributes || action.payload?.data || {};
      })
      .addCase(updatePaymentRequest.fulfilled, (state, action) => {
        state.recordData = action.payload?.data?.attributes || action.payload?.data || {};
      })
      .addCase(submitPaymentRequest.fulfilled, (state, action) => {
        state.recordData = action.payload?.data?.attributes || action.payload?.data || {};
      })
      .addCase(withdrawPaymentRequest.fulfilled, (state, action) => {
        state.recordData = action.payload?.data?.attributes || action.payload?.data || {};
      })
      .addCase(approvePaymentRequest.fulfilled, (state, action) => {
        state.recordData = action.payload?.data?.attributes || action.payload?.data || {};
      })
      .addCase(rejectPaymentRequest.fulfilled, (state, action) => {
        state.recordData = action.payload?.data?.attributes || action.payload?.data || {};
      })
      .addCase(cancelPaymentRequest.fulfilled, (state, action) => {
        state.recordData = action.payload?.data?.attributes || action.payload?.data || {};
      })
      .addCase(submitPaymentRequestErp.fulfilled, (state, action) => {
        state.recordData = action.payload?.data?.attributes || action.payload?.data || {};
      });

    addRejectedMatcher(builder, "PaymentRequest");
  },
});

export const { clearPaymentRequest } = paymentRequestSlice.actions;
export default paymentRequestSlice.reducer;
